import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageSEO from "@/components/base/PageSEO";

type FormState = "idle" | "submitting" | "success" | "error";

export default function Contact() {
  const [formState, setFormState] = useState<FormState>("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement)?.value || "";
    if (message.length > 500) return;
    setFormState("submitting");
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((val, key) => { if (typeof val === "string") data.append(key, val); });
    try {
      const res = await fetch("https://readdy.ai/api/form/d7f2lcia509fjl5l0360", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setFormState(res.ok ? "success" : "error");
    } catch {
      setFormState("error");
    }
  };

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement)?.value || "";
    if (message.length > 500) return;
    setFormState("submitting");
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((val, key) => { if (typeof val === "string") data.append(key, val); });
    try {
      const res = await fetch("https://readdy.ai/api/form/d7f2qpqa509fjl5l03bg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setFormState(res.ok ? "success" : "error");
    } catch {
      setFormState("error");
    }
  };

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <PageSEO
        title="門市資訊｜台中西屯區設計師女裝Outlet 聯絡預約"
        description="台中設計師女裝Outlet門市位於西屯區台灣大道，週二至週日11:00-19:30營業，歡迎預約試穿或來店體驗高質感熟齡女裝。"
        keywords="台中女裝門市,台中西屯區服飾店,設計師品牌預約,台中Outlet門市地址"
        canonical="https://www.example.com/contact"
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative h-[45vh] min-h-[340px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=elegant%20women%20fashion%20boutique%20interior%20warm%20cream%20beige%20rose%20tones%20soft%20natural%20light%20luxury%20store%20atmosphere%20designer%20clothing%20display%20minimalist&width=1400&height=650&seq=contacthero&orientation=landscape"
            alt="門市資訊"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/55" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <p className="text-[#f5d8b8] text-[1rem] tracking-[0.3em] uppercase font-medium mb-3">Visit Us</p>
            <h1 className="text-white text-[2.75rem] md:text-[3.25rem] font-bold leading-[1.3] mb-3">門市資訊 / 聯絡我們</h1>
            <p className="text-white/80 text-[1.1875rem] max-w-xl leading-[1.75]">台中實體門市歡迎您親自到訪，感受每件服飾的質感與版型魅力。</p>
          </div>
        </section>

        {/* Store Info + Map */}
        <section className="py-16 md:py-24 bg-[#faf8f5]">
          <div className="w-full px-6 md:px-16 max-w-[1400px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
              {/* Info */}
              <div>
                <p className="text-[#8B5E52] text-[1rem] tracking-[0.25em] uppercase font-medium mb-3">Store Information</p>
                <h2 className="text-[#3d2b1f] text-[2.125rem] md:text-[2.5rem] font-bold leading-[1.4] mb-8">台中實體門市</h2>

                <div className="flex flex-col gap-6 mb-10">
                  {[
                    { icon: "ri-map-pin-2-line", label: "地址", value: "台中市西屯區台灣大道三段XXX號\n（近捷運台灣大道站，步行約5分鐘）" },
                    { icon: "ri-phone-line", label: "電話", value: "04-XXXX-XXXX", href: "tel:+886-4-XXXX-XXXX" },
                    { icon: "ri-time-line", label: "營業時間", value: "週二至週日 11:00–19:30\n週一公休" },
                    { icon: "ri-car-line", label: "停車資訊", value: "門市附近設有公共停車場，停車便利" },
                    { icon: "ri-line-fill", label: "LINE 諮詢", value: "加入 LINE 官方帳號，隨時線上諮詢", href: "https://line.me", external: true },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#f5e8d8] flex-shrink-0">
                        <i className={`${item.icon} text-[#8B5E52] text-lg`}></i>
                      </div>
                      <div>
                        <p className="text-[#3d2b1f] text-[1rem] font-semibold mb-0.5">{item.label}</p>
                        {item.href ? (
                          <a
                            href={item.href}
                            target={item.external ? "_blank" : undefined}
                            rel={item.external ? "noopener noreferrer nofollow" : "nofollow"}
                            className="text-[#8B5E52] text-[1.0625rem] hover:underline cursor-pointer"
                          >
                            {item.value}
                          </a>
                        ) : (
                          <p className="text-[#7a6455] text-[1.0625rem] leading-[1.75] whitespace-pre-line">{item.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Store Photos */}
                <div className="grid grid-cols-2 gap-3">
                  <img src="https://readdy.ai/api/search-image?query=elegant%20women%20fashion%20boutique%20interior%20cream%20warm%20tones%20clothing%20display%20rack%20minimalist%20luxury%20designer%20store%20natural%20light&width=400&height=280&seq=store1&orientation=landscape" alt="門市環境1" className="w-full h-[160px] object-cover object-top rounded-lg" />
                  <img src="https://readdy.ai/api/search-image?query=elegant%20fashion%20boutique%20fitting%20room%20interior%20warm%20beige%20rose%20tones%20luxury%20designer%20store%20soft%20lighting%20minimalist%20quality&width=400&height=280&seq=store2&orientation=landscape" alt="門市環境2" className="w-full h-[160px] object-cover object-top rounded-lg" />
                </div>
              </div>

              {/* Map */}
              <div className="flex flex-col gap-6">
                <div className="rounded-lg overflow-hidden h-[400px]">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3640.5!2d120.6478!3d24.1694!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjTCsDEwJzEwLjAiTiAxMjDCsDM4JzUyLjEiRQ!5e0!3m2!1szh-TW!2stw!4v1"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="台中門市地圖"
                    className="rounded-lg"
                  />
                </div>
                {/* LINE QR Area */}
                <div className="bg-[#f5ede4] rounded-lg p-6 flex items-center gap-5">
                  <div className="w-24 h-24 flex-shrink-0 bg-white rounded-lg flex items-center justify-center border border-[#e0d4c8]">
                    <i className="ri-qr-code-line text-[#3d2b1f] text-5xl"></i>
                  </div>
                  <div>
                    <p className="text-[#3d2b1f] text-[1.125rem] font-bold mb-1">掃描加入 LINE 官方帳號</p>
                    <p className="text-[#7a6455] text-[1.0625rem] leading-[1.75]">即時接收新品通知、優惠活動，<br />並可直接線上諮詢造型建議。</p>
                    <a href="https://line.me" target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1.5 mt-2 text-[#8B5E52] text-[1rem] font-semibold hover:underline cursor-pointer">
                      <i className="ri-line-fill text-[#06C755]"></i> 點此加入 LINE
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Booking Form + Contact Form */}
        <section className="py-16 md:py-20 bg-[#f5ede4]">
          <div className="w-full px-6 md:px-16 max-w-[1400px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Booking Form */}
              <div className="bg-white rounded-lg p-8 md:p-10">
                <p className="text-[#8B5E52] text-[1rem] tracking-[0.2em] uppercase font-medium mb-2">Book a Fitting</p>
                <h2 className="text-[#3d2b1f] text-[1.625rem] font-bold mb-2">預約試穿</h2>
                <p className="text-[#7a6455] text-[1.0625rem] mb-7 leading-[1.75]">填寫以下資料，我們將在24小時內與您確認預約時間。</p>

                {formState === "success" ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#f5e8d8] mx-auto mb-4">
                      <i className="ri-check-line text-[#8B5E52] text-2xl"></i>
                    </div>
                    <h3 className="text-[#3d2b1f] text-[1.25rem] font-bold mb-2">預約成功！</h3>
                    <p className="text-[#7a6455] text-[1.0625rem] leading-[1.75]">感謝您的預約，我們將盡快與您聯繫確認時間。</p>
                    <button onClick={() => setFormState("idle")} className="mt-6 px-6 py-2.5 bg-[#f5ede4] text-[#3d2b1f] rounded-full text-[1rem] font-medium cursor-pointer hover:bg-[#e8d8c8] transition-colors whitespace-nowrap">再次預約</button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form id="booking-form" className="flex flex-col gap-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[#3d2b1f] text-[1rem] font-medium mb-1.5">姓名 <span className="text-[#8B5E52]">*</span></label>
                        <input name="name" type="text" required placeholder="您的姓名" className="w-full border border-[#e0d4c8] rounded-md px-4 py-3 text-[1rem] text-[#3d2b1f] placeholder-[#c0b0a0] outline-none focus:border-[#8B5E52] transition-colors bg-[#faf8f5]" />
                      </div>
                      <div>
                        <label className="block text-[#3d2b1f] text-[1rem] font-medium mb-1.5">電話 <span className="text-[#8B5E52]">*</span></label>
                        <input name="phone" type="tel" required placeholder="聯絡電話" className="w-full border border-[#e0d4c8] rounded-md px-4 py-3 text-[1rem] text-[#3d2b1f] placeholder-[#c0b0a0] outline-none focus:border-[#8B5E52] transition-colors bg-[#faf8f5]" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[#3d2b1f] text-[1rem] font-medium mb-1.5">電子信箱</label>
                      <input name="email" type="email" placeholder="your@email.com" className="w-full border border-[#e0d4c8] rounded-md px-4 py-3 text-[1rem] text-[#3d2b1f] placeholder-[#c0b0a0] outline-none focus:border-[#8B5E52] transition-colors bg-[#faf8f5]" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[#3d2b1f] text-[1rem] font-medium mb-1.5">希望到訪日期 <span className="text-[#8B5E52]">*</span></label>
                        <input name="date" type="date" required className="w-full border border-[#e0d4c8] rounded-md px-4 py-3 text-[1rem] text-[#3d2b1f] outline-none focus:border-[#8B5E52] transition-colors bg-[#faf8f5] cursor-pointer" />
                      </div>
                      <div>
                        <label className="block text-[#3d2b1f] text-[1rem] font-medium mb-1.5">偏好時段</label>
                        <select name="time_preference" className="w-full border border-[#e0d4c8] rounded-md px-4 py-3 text-[1rem] text-[#3d2b1f] outline-none focus:border-[#8B5E52] transition-colors bg-[#faf8f5] cursor-pointer">
                          <option value="">請選擇</option>
                          <option value="morning">上午 11:00–13:00</option>
                          <option value="afternoon">下午 13:00–16:00</option>
                          <option value="evening">傍晚 16:00–19:30</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-[#3d2b1f] text-[1rem] font-medium mb-1.5">有興趣的商品類別</label>
                      <select name="category" className="w-full border border-[#e0d4c8] rounded-md px-4 py-3 text-[1rem] text-[#3d2b1f] outline-none focus:border-[#8B5E52] transition-colors bg-[#faf8f5] cursor-pointer">
                        <option value="">請選擇（可略）</option>
                        <option value="dress">洋裝系列</option>
                        <option value="jacket">外套系列</option>
                        <option value="tops">上衣系列</option>
                        <option value="pants">褲裝系列</option>
                        <option value="suit">套裝系列</option>
                        <option value="all">全品項瀏覽</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[#3d2b1f] text-[1rem] font-medium mb-1.5">備註</label>
                      <textarea
                        name="message"
                        rows={3}
                        maxLength={500}
                        placeholder="如有特別需求或想了解的商品，請在此說明（500字以內）"
                        onChange={e => setCharCount(e.target.value.length)}
                        className="w-full border border-[#e0d4c8] rounded-md px-4 py-3 text-[1rem] text-[#3d2b1f] placeholder-[#c0b0a0] outline-none focus:border-[#8B5E52] transition-colors bg-[#faf8f5] resize-none"
                      />
                      <p className="text-[#a09080] text-[0.875rem] text-right mt-1">{charCount}/500</p>
                    </div>
                    <button
                      type="submit"
                      disabled={formState === "submitting"}
                      className="w-full py-4 bg-[#3d2b1f] text-[#faf8f5] rounded-full text-[1.0625rem] font-bold hover:bg-[#8B5E52] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                    >
                      {formState === "submitting" ? "送出中..." : "確認預約試穿"}
                    </button>
                    {formState === "error" && <p className="text-red-500 text-[1rem] text-center">送出失敗，請稍後再試或加入 LINE 聯絡我們。</p>}
                  </form>
                )}
              </div>

              {/* Contact Form */}
              <div className="bg-white rounded-lg p-8 md:p-10">
                <p className="text-[#8B5E52] text-[1rem] tracking-[0.2em] uppercase font-medium mb-2">Contact Us</p>
                <h2 className="text-[#3d2b1f] text-[1.625rem] font-bold mb-2">聯絡我們</h2>
                <p className="text-[#7a6455] text-[1.0625rem] mb-7 leading-[1.75]">有任何問題或建議，歡迎留言，我們將盡快回覆您。</p>

                <form onSubmit={handleContactSubmit} data-readdy-form id="contact-form" className="flex flex-col gap-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#3d2b1f] text-[1rem] font-medium mb-1.5">姓名 <span className="text-[#8B5E52]">*</span></label>
                      <input name="name" type="text" required placeholder="您的姓名" className="w-full border border-[#e0d4c8] rounded-md px-4 py-3 text-[1rem] text-[#3d2b1f] placeholder-[#c0b0a0] outline-none focus:border-[#8B5E52] transition-colors bg-[#faf8f5]" />
                    </div>
                    <div>
                      <label className="block text-[#3d2b1f] text-[1rem] font-medium mb-1.5">電話</label>
                      <input name="phone" type="tel" placeholder="聯絡電話（可略）" className="w-full border border-[#e0d4c8] rounded-md px-4 py-3 text-[1rem] text-[#3d2b1f] placeholder-[#c0b0a0] outline-none focus:border-[#8B5E52] transition-colors bg-[#faf8f5]" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[#3d2b1f] text-[1rem] font-medium mb-1.5">電子信箱 <span className="text-[#8B5E52]">*</span></label>
                    <input name="email" type="email" required placeholder="your@email.com" className="w-full border border-[#e0d4c8] rounded-md px-4 py-3 text-[1rem] text-[#3d2b1f] placeholder-[#c0b0a0] outline-none focus:border-[#8B5E52] transition-colors bg-[#faf8f5]" />
                  </div>
                  <div>
                    <label className="block text-[#3d2b1f] text-[1rem] font-medium mb-1.5">詢問主題</label>
                    <select name="subject" className="w-full border border-[#e0d4c8] rounded-md px-4 py-3 text-[1rem] text-[#3d2b1f] outline-none focus:border-[#8B5E52] transition-colors bg-[#faf8f5] cursor-pointer">
                      <option value="product">商品詢問</option>
                      <option value="size">尺寸諮詢</option>
                      <option value="booking">預約相關</option>
                      <option value="return">退換貨問題</option>
                      <option value="other">其他</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[#3d2b1f] text-[1rem] font-medium mb-1.5">留言內容 <span className="text-[#8B5E52]">*</span></label>
                    <textarea
                      name="message"
                      rows={5}
                      required
                      maxLength={500}
                      placeholder="請描述您的問題或需求（500字以內）"
                      className="w-full border border-[#e0d4c8] rounded-md px-4 py-3 text-[1rem] text-[#3d2b1f] placeholder-[#c0b0a0] outline-none focus:border-[#8B5E52] transition-colors bg-[#faf8f5] resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={formState === "submitting"}
                    className="w-full py-4 bg-[#8B5E52] text-[#faf8f5] rounded-full text-[1.0625rem] font-bold hover:bg-[#3d2b1f] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {formState === "submitting" ? "送出中..." : "送出留言"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
